/**
 * Role-Based Access Control (RBAC) Type Definitions
 * Matches backend API models for roles, permissions, and authorization
 */

/**
 * Predefined role IDs (must match backend RoleIds constant)
 */
export const RoleIds = {
  Viewer: 1,
  Contributor: 2,
  TenantAdmin: 3,
  LicenceAdmin: 4,
  DatabaseAdmin: 5,
  PresetAdmin: 6,
  IdentityAdmin: 7,
  Administrator: 8
} as const

export type RoleId = typeof RoleIds[keyof typeof RoleIds]

/**
 * Core role interface matching backend Role entity
 */
export interface Role {
  id: number
  name: string
  description: string
  scopes: string[]
  isActive: boolean
  createdAt: string
  updatedAt: string
}

/**
 * Scope pattern for resource:operation:path format
 * Examples:
 * - "presets:read" - Read all presets
 * - "presets:write" - Write all presets
 * - "presets:write:database" - Write only database properties in presets
 * - "presets:write:database.*" - Write database and nested properties
 * - "*:*" - Full access to all resources and operations
 */
export type RoleScope = string

/**
 * Role data transfer object (matches backend API responses)
 */
export interface RoleDto {
  id: number
  name: string
  description: string
  scopes: RoleScope[]
  isActive: boolean
  createdAt: string
  updatedAt: string
}

/**
 * Request model for creating a new role
 */
export interface CreateRoleRequest {
  name: string
  description: string
  scopes: RoleScope[]
}

/**
 * Request model for updating an existing role
 */
export interface UpdateRoleRequest {
  name: string
  description: string
  scopes: RoleScope[]
  isActive: boolean
}

/**
 * User with role information (matches backend API response)
 */
export interface UserWithRoles {
  id: number
  email: string
  displayName: string
  roles: number[] // Array of role IDs
  isActive: boolean
  updatedAt: string
}

/**
 * Subscriber (API key) with role information (matches backend API response)
 */
export interface SubscriberWithRoles {
  id: number
  keyName: string
  roles: number[] // Array of role IDs
  description?: string
  isActive: boolean
  createdAt: string
  updatedAt: string
  expiresAt?: string
}

/**
 * Request model for assigning roles to a user
 */
export interface AssignRolesRequest {
  roleIds: number[]
}

/**
 * Request model for removing roles from a user
 */
export interface RemoveRolesRequest {
  roleIds: number[]
}

/**
 * Permission check response
 */
export interface HasPermissionResponse {
  hasPermission: boolean
  resource: string
  operation: string
  path: string | null
  roles: number[]
  matchedScopes: string[]
}

/**
 * Accessible resource information
 */
export interface AccessibleResource {
  resource: string
  operation: string
  paths: string[]
  scope: string
}

/**
 * Accessible resources response
 */
export interface AccessibleResourcesResponse {
  operation: string
  resources: AccessibleResource[]
  roles: number[]
  totalScopes: number
}

/**
 * Allowed operations response
 */
export interface AllowedOperationsResponse {
  resource: string
  path: string | null
  allowedOperations: string[]
  hasWildcard: boolean
  roles: number[]
  matchedScopes: string[]
}

/**
 * Current user permissions summary
 */
export interface PermissionsSummary {
  identifier: string
  authenticationType: 'JWT' | 'SubscriptionKey'
  roles: RoleDto[]
  scopes: RoleScope[]
  isAuthenticated: boolean
}

/**
 * Resource types for permission checking
 */
export const ResourceTypes = {
  Presets: 'presets',
  Tenants: 'tenants',
  Schemas: 'schemas',
  Roles: 'roles',
  Users: 'users',
  Subscribers: 'subscribers'
} as const

export type ResourceType = typeof ResourceTypes[keyof typeof ResourceTypes]

/**
 * Operation types for permission checking
 */
export const OperationTypes = {
  Read: 'read',
  Write: 'write',
  Create: 'create',
  Delete: 'delete'
} as const

export type OperationType = typeof OperationTypes[keyof typeof OperationTypes]

/**
 * Helper type for scope pattern matching
 */
export interface ScopePattern {
  resource: string
  operation: string
  path?: string
}

/**
 * Permission evaluation context
 */
export interface PermissionContext {
  resource: ResourceType | string
  operation: OperationType | string
  path?: string
  userRoles: number[]
  userScopes: RoleScope[]
}
